// IM-XPR-001 (IM-UI-092, IM-UX-109) — Shell da experiência orientada a objetivos: cinco entradas no menu
// principal (Hoje, Meus objetivos, Acompanhar, Análises e dados, Configurações). Os módulos técnicos saem do menu, mas
// continuam nas mesmas rotas (Análises e dados, objetivos, busca global, links contextuais e URLs antigas). Reaproveita
// o seletor de organização, o aviso de contexto e a busca do shell anterior; no celular, barra inferior com quatro áreas.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { OBJECTIVES } from '@intelimove/contracts';
import { ROLE_LABEL, useAuth } from '../auth/AuthContext';
import { BrandLogo } from '../components/BrandLogo';
import { ContextBanner, GlobalSearch, OrganizationSwitcher, type SearchResult } from '../components/AppShell';
import { Icon, type IconName } from '../components/Icon';
import { initials } from '../components/Widgets';
import { CAPABILITY_PREFIXES, searchCapabilities } from './catalog';
import { ReturnBar } from './returnTo';

interface Entry { to: string; label: string; short: string; icon: IconName; match: (path: string) => boolean }
const under = (path: string, prefix: string) => path === prefix || path.startsWith(`${prefix}/`);
export const MAIN_NAV: Array<{ group: string; items: Entry[] }> = [
  { group: 'Seu trabalho', items: [
    { to: '/', label: 'Hoje', short: 'Hoje', icon: 'today', match: (p) => p === '/' },
    { to: '/objetivos', label: 'Meus objetivos', short: 'Objetivos', icon: 'goal', match: (p) => under(p, '/objetivos') || under(p, '/jornadas') },
    { to: '/acompanhar', label: 'Acompanhar', short: 'Acompanhar', icon: 'signals', match: (p) => p === '/acompanhar' },
  ] },
  { group: 'Apoio', items: [
    { to: '/analises', label: 'Análises e dados', short: 'Dados', icon: 'grid', match: (p) => p === '/analises' || CAPABILITY_PREFIXES.some((x) => x !== '/jornadas' && under(p, x)) },
    { to: '/configuracoes', label: 'Configurações', short: 'Ajustes', icon: 'settings', match: (p) => under(p, '/configuracoes') || under(p, '/empresas') },
  ] },
];
const ENTRIES = MAIN_NAV.flatMap((g) => g.items);
/** Nome da área atual, para a trilha do topo. */
export function areaOf(path: string): string { return ENTRIES.find((e) => e.match(path))?.label ?? 'InteliMove'; }

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
function extraResults(q: string): SearchResult[] {
  const t = norm(q.trim());
  const goals = t.length < 2 ? [] : OBJECTIVES.filter((o) => norm(`${o.title} ${o.outcome}`).includes(t)).slice(0, 3).map((o) => ({ kind: 'Objetivo' as const, label: o.title, to: `/objetivos/${o.key}` }));
  return [...goals, ...searchCapabilities(q, 4).map((c) => ({ kind: 'Análise' as const, label: c.name, to: c.to }))];
}

export function ShellV2() {
  const { me, logout } = useAuth();
  const { pathname } = useLocation();
  const [drawer, setDrawer] = useState(false);
  const sidebar = useRef<HTMLElement>(null);
  const menuBtn = useRef<HTMLButtonElement>(null);
  const orgId = me?.activeOrganization?.id ?? 'none';
  const extra = useCallback(extraResults, []);

  useEffect(() => { setDrawer(false); }, [pathname]);
  useEffect(() => {
    if (!drawer) return;
    sidebar.current?.querySelector<HTMLElement>('button:not([disabled]), a[href]')?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setDrawer(false); menuBtn.current?.focus(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawer]);

  const user = me?.user;
  const role = me?.activeOrganization?.role;
  return (
    <div className="app xp-app">
      <a href="#conteudo" className="skip-link">Pular para o conteúdo</a>
      <aside className={`sidebar ${drawer ? 'open' : ''}`} id="sidebar" ref={sidebar} aria-label="Navegação principal">
        <BrandLogo variant="sidebar" />
        <OrganizationSwitcher />
        <nav className="nav xp-nav" aria-label="Menu principal">
          {MAIN_NAV.map((g) => (
            <div key={g.group} style={{ display: 'contents' }}>
              <span className="nav-label">{g.group}</span>
              {g.items.map((i) => {
                const active = i.match(pathname);
                return <Link key={i.to} to={i.to} className={active ? 'active' : ''} aria-current={active ? 'page' : undefined}><Icon name={i.icon} />{i.label}</Link>;
              })}
            </div>
          ))}
        </nav>
        <div className="side-foot">
          <div className="avatar" aria-hidden="true">{user ? initials(user.name) : ''}</div>
          <div className="who"><strong>{user?.name}</strong><small>{role ? ROLE_LABEL[role] : 'Sem organização'}</small></div>
          <button type="button" className="logout" onClick={logout} aria-label="Sair da InteliMove" title="Sair"><Icon name="logout" /></button>
        </div>
      </aside>
      {drawer && <button type="button" className="scrim" aria-label="Fechar menu" onClick={() => setDrawer(false)} />}
      <main className="main">
        <header className="topbar">
          <button ref={menuBtn} type="button" className="icon-btn mobile-menu" aria-label={drawer ? 'Fechar menu' : 'Abrir menu'} aria-expanded={drawer} aria-controls="sidebar" onClick={() => setDrawer((d) => !d)}><Icon name="menu" /></button>
          <p className="xp-crumb"><span>{me?.activeOrganization?.name}</span><span aria-hidden="true">›</span><strong>{areaOf(pathname)}</strong></p>
          <GlobalSearch extra={extra} placeholder="Buscar objetivo, análise ou sinal" />
          <div className="top-actions">
            <Link className="secondary btn-link xp-new" to="/objetivos">+ Começar algo novo</Link>
          </div>
        </header>
        <div className="content xp-content" id="conteudo" tabIndex={-1}>
          {me?.activeOrganization?.purpose && me.activeOrganization.purpose !== 'REAL' && (
            <p className="notice-inline org-purpose" role="note"><b>{me.activeOrganization.purpose === 'DEMONSTRACAO' ? 'Organização de demonstração' : 'Organização técnica de teste'}.</b> Os dados desta organização podem ser fictícios ou de teste e não representam inteligência comercial real.</p>
          )}
          {orgId !== 'none' && <ContextBanner orgId={orgId} />}
          <ReturnBar />
          <Outlet key={orgId} />
        </div>
      </main>
      <nav className="xp-mobile-bar" aria-label="Navegação rápida">
        {ENTRIES.slice(0, 4).map((i) => {
          const active = i.match(pathname);
          return <Link key={i.to} to={i.to} className={active ? 'active' : ''} aria-current={active ? 'page' : undefined}><Icon name={i.icon} /><span>{i.short}</span></Link>;
        })}
      </nav>
    </div>
  );
}
